import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CommonTable from "../../components/commonComponent/CommanTable";
import axiosInstance from "../../utils/axiosInstance";
import { toastSuccess, toastError } from "../../utils/alertHelper";

// Table columns for variants
const variantColumns = [
  { key: "srNo", label: "Sr No." },
  { key: "name", label: "Name" },
  { key: "companyName", label: "Company Name" },
  { key: "unit", label: "Unit" },
  { key: "hsn", label: "HSN" },
  { key: "rate", label: "Rate" },
  { key: "gst", label: "GST %" },
  { key: "description", label: "Description" },
  { key: "isAvailable", label: "Available" }
];

// IMAGE handling removed per request

export default function ItemView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  // Fetch item data from API
  const fetchItem = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.get(`/api/item/${id}`);

      if (response.data.Status === 1) {
        setItem(response.data.item);
        toastSuccess(response.data.Message || 'Item fetched successfully');
      } else {
        const errorMessage = response.data.Message || "Failed to fetch item";
        setError(errorMessage);
        toastError(errorMessage);
      }
    } catch (error) {
      console.error("Error fetching item:", error);
      const errorMessage = error.response?.data?.Message || "Failed to fetch item. Please try again.";
      setError(errorMessage);
      toastError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchItem();
    }
  }, [id]);

  // Prepare variant data for table
  const variantRows = item?.variants?.map((variant, index) => ({
    ...variant,
    srNo: index + 1,
    unit: variant.unit?.name || variant.unit || "N/A",
    rate: variant.rate ? `₹${variant.rate}` : "N/A",
    gst: variant.gst ? `${variant.gst}%` : "N/A",
    description: variant.description || "N/A",
    isAvailable: (
      <span className={`badge px-2 py-1 rounded-pill ${variant.isAvailable ? "bg-success" : "bg-danger"}`}>
        {variant.isAvailable ? "Yes" : "No"}
      </span>
    )
  })) || [];

  const filteredRows = variantRows.filter(row =>
    Object.values(row)
      .join(" ")
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="container py-4">
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading item details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => navigate('/user/item')}
        >
          <i className="fas fa-arrow-left me-1"></i> Back to Items
        </button>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="container py-4">
        <div className="text-center py-5">
          <div className="mb-3">
            <i className="fas fa-inbox fa-3x text-muted"></i>
          </div>
          <h5 className="text-muted">Item not found</h5>
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/user/item')}
          >
            <i className="fas fa-arrow-left me-1"></i> Back to Items
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4" style={{ fontFamily: "inherit", maxWidth: 1200 }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="fw-bold" style={{ fontSize: "2rem" }}>Item Details</h1>
        <a href="/user/item" className="text-decoration-none text-secondary">
            &larr; Item
          </a>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-md-10">
          <div className="card p-3 border-1 shadow-sm rounded-3" style={{ minHeight: 150 }}>
            <div className="fw-semibold mb-3" style={{ fontSize: "1rem" }}>Item Information</div>
            <div className="row">
              <div className="col-md-6 mb-2">
                <div className="mb-2"><span className="text-muted">Item Code:</span> <span className="text-muted">{item.code}</span></div>
                <div className="mb-2"><span className="text-muted">Name:</span> <span className="text-muted">{item.name}</span></div>
                <div className="mb-2">
                  <span className="text-muted">Available:</span> 
                  <span className={`badge ms-2 px-2 py-1 rounded-pill ${item.isAvailable ? "bg-success" : "bg-danger"}`}>
                    {item.isAvailable ? "Yes" : "No"}
                  </span>
                </div>
              </div>
              <div className="col-md-6 mb-2">
                <div className="mb-2">
                  <span className="text-muted">Show Item:</span> 
                  <span className={`badge ms-2 px-2 py-1 rounded-pill ${item.isshow ? "bg-info" : "bg-secondary"}`}>
                    {item.isshow ? "Yes" : "No"}
                  </span>
                </div>
                {/* <div className="mb-2">
                  <span className="text-muted">Show Image:</span> 
                  <span className={`badge ms-2 px-2 py-1 rounded-pill ${item.isshowImage ? "bg-info" : "bg-secondary"}`}>
                    {item.isshowImage ? "Yes" : "No"}
                  </span>
                </div> */}
                <div className="mb-2">
                  <span className="text-muted">Created:</span> 
                  <span className="text-muted">{new Date(item.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="fw-semibold mb-0" style={{ fontSize: "1.35rem" }}>
            Variant Information 
            <span className="badge bg-primary ms-2">{variantRows.length}</span>
          </h3>
          <input
            type="text"
            className="form-control"
            style={{ maxWidth: 280 }}
            placeholder="Search variants..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {variantRows.length === 0 ? (
          <div className="text-center py-5">
            <div className="mb-3">
              <i className="fas fa-cube fa-3x text-muted"></i>
            </div>
            <h5 className="text-muted">No variants available</h5>
            <p className="text-muted">This item doesn't have any variants yet.</p>
          </div>
        ) : (
          <CommonTable
            columns={variantColumns}
            data={filteredRows}
            itemsPerPageOptions={[10, 15, 25]}
            defaultItemsPerPage={15}
            bordered
          />
        )}
      </div>
    </div>
  );
}